// Müşteri kaydı KPI kartı ipucu (sektör paketleri DR18/DR21): set yoksa ve müşteri sektörüne
// eşleşen etkin paket varsa tek satır "Bu sektör için hazır paket var: <paket> (N) — Set kur".
// "Set kur" Set kur penceresini önerilen paket işaretli açar (DR2 istisnası); "Kapat" kalıcı
// (`localStorage["kpi.pkgHint.<userId>.<clientId>"]`); set kurulunca kendiliğinden kalkar.
// Paket listesi yalnız ipucu koşulu sağlanabilecekse çekilir (set yok + sektör çözülüyor).

import { useEffect, useMemo, useState } from "react";
import { normalizeSectorCode, type KpiPackage } from "@mahir/shared";
import type { StorageLike } from "../../lib/kpi-catalog-sync";
import {
  dismissPackageHint,
  isPackageHintDismissed,
  packageHintStorageKey,
  shouldShowPackageHint,
} from "../../lib/kpi-package-hint";
import { KPI_PACKAGES_COPY } from "../../lib/kpi-packages-copy";
import { useAuthStore } from "../../stores/auth-store";
import { useKpiPackagesStore } from "../../stores/kpi-packages-store";
import { ForkSetModal } from "../kpi/ForkSetModal";

const H = KPI_PACKAGES_COPY.hint;

function storage(): StorageLike | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

export interface ClientPackageHintProps {
  clientId: string;
  sector: string | null | undefined;
  /** müşterinin set sayısı; null = yükleniyor */
  setCount: number | null;
}

export function ClientPackageHint(props: ClientPackageHintProps): JSX.Element | null {
  const { clientId, sector, setCount } = props;
  const user = useAuthStore((s) => s.user);
  const canFork = user?.permissions["kpi.manage"] === true;
  const packages = useKpiPackagesStore((s) => s.packages);
  const loadPackages = useKpiPackagesStore((s) => s.loadPackages);
  const key = useMemo(
    () => packageHintStorageKey(user?.id ?? "anon", clientId),
    [user?.id, clientId],
  );
  const [dismissed, setDismissed] = useState(() => isPackageHintDismissed(storage(), key));
  const [forkOpen, setForkOpen] = useState(false);
  const eligible = canFork && setCount === 0 && normalizeSectorCode(sector) !== null;

  useEffect(() => {
    setDismissed(isPackageHintDismissed(storage(), key));
  }, [key]);
  useEffect(() => {
    if (eligible && !dismissed) void loadPackages();
  }, [eligible, dismissed, loadPackages]);

  const pkg = shouldShowPackageHint({ setCount, sector, packages, dismissed, canFork });
  if (!pkg && !forkOpen) return null;

  return (
    <>
      {pkg ? (
        <ClientPackageHintLine
          pkg={pkg}
          onSetUp={() => setForkOpen(true)}
          onDismiss={() => {
            dismissPackageHint(storage(), key);
            setDismissed(true);
          }}
        />
      ) : null}
      <ForkSetModal
        open={forkOpen}
        initialClientId={clientId}
        initialPackageId={pkg?.id ?? null}
        onClose={() => setForkOpen(false)}
      />
    </>
  );
}

/** İpucu satırı (saf görünüm — SSR testi). */
export function ClientPackageHintLine(props: {
  pkg: Pick<KpiPackage, "name" | "itemCount">;
  onSetUp: () => void;
  onDismiss: () => void;
}): JSX.Element {
  return (
    <div
      data-testid="client-package-hint"
      className="flex items-center gap-2 flex-wrap text-sm mb-3"
      style={{ color: "var(--color-text-2)" }}
    >
      <span>{H.clientSectorText(props.pkg.name, props.pkg.itemCount)}</span>
      <span aria-hidden="true">—</span>
      <button type="button" onClick={props.onSetUp} style={linkBtn}>
        {H.setUp}
      </button>
      <button
        type="button"
        aria-label={H.dismissLabel}
        onClick={props.onDismiss}
        style={{ ...linkBtn, color: "var(--color-text-3)", fontWeight: 400 }}
      >
        {H.dismiss}
      </button>
    </div>
  );
}

const linkBtn = {
  border: 0,
  background: "transparent",
  padding: "0 4px",
  minHeight: 44,
  cursor: "pointer",
  color: "var(--color-primary-text)",
  fontWeight: 600,
  textDecoration: "underline",
} as const;
